import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell, CheckCircle2, Heart, QrCode, ShieldCheck, Ticket } from "lucide-react";

import VibeBeeHomepagePromo from "./VibeBeeHomepagePromo";
import { trackEvent } from "../lib/analytics";
import { trackPlatformEvent } from "../lib/platformAnalytics";

const BENEFITS = [
  { label: "Save favourite places", icon: <Heart size={15} /> },
  { label: "Live Vibe Alerts", icon: <Bell size={15} /> },
  { label: "Verified QR check-ins", icon: <QrCode size={15} /> },
  { label: "Trusted Vibe Pass Points", icon: <ShieldCheck size={15} /> },
];

export default function VibePassHomepagePromo({ inline = false }) {
  const location = useLocation();
  const tracked = React.useRef(false);
  const isHomepagePromo = inline && location.pathname === "/";

  React.useEffect(() => {
    if (!isHomepagePromo || tracked.current) return;

    tracked.current = true;
    trackEvent("vibe_pass_promo_view", {
      page_path: "/",
      source_section: "homepage_promo",
    });
    void trackPlatformEvent("vibe_pass_promo_view", {
      page_path: "/",
      category: "vibe_pass",
      metadata_json: { source_section: "homepage_promo" },
    });
  }, [isHomepagePromo]);

  if (!isHomepagePromo) return null;

  return (
    <>
      <VibeBeeHomepagePromo />

      <section className="relative mt-7 overflow-hidden rounded-[30px] border border-[#f0c66a]/30 bg-[#120f12]/82 p-5 text-white shadow-[0_28px_80px_rgba(0,0,0,0.36)] backdrop-blur-2xl sm:p-6 lg:p-7">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#f0c66a]/70 to-transparent" />
        <div className="absolute -right-24 -top-24 h-56 w-56 rounded-full bg-fuchsia-500/14 blur-[90px]" />
        <div className="absolute -bottom-24 left-8 h-52 w-52 rounded-full bg-[#f0c66a]/12 blur-[85px]" />

        <div className="relative z-10 grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#f0c66a]/25 bg-[#f0c66a]/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-[#f0c66a]">
              <Ticket size={14} />
              Vibe Pass Pilot
            </div>

            <h2 className="text-3xl font-black leading-tight text-white sm:text-4xl">
              Join free and get ready for live local offers.
            </h2>

            <p className="mt-3 max-w-2xl text-sm leading-6 text-white/72 sm:text-base">
              Vibe Pass helps visitors follow favourite places, receive Vibe Alerts and earn Trusted Vibe Pass Points through qualifying verified pilot activity.
            </p>

            <p className="mt-3 text-xs leading-5 text-white/52">
              Free pilot access. No payment today. Vibe Points have no cash value and rewards are only available when participating venues activate them.
            </p>
          </div>

          <div className="rounded-[24px] border border-white/10 bg-black/32 p-4">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {BENEFITS.map((benefit) => (
                <div key={benefit.label} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.06] px-3 py-2.5 text-sm font-bold text-white/82">
                  <span className="text-[#f0c66a]">{benefit.icon}</span>
                  <span>{benefit.label}</span>
                </div>
              ))}
            </div>

            <Link
              to="/vibe-pass"
              onClick={() => {
                trackEvent("vibe_pass_promo_click", {
                  page_path: "/",
                  destination_url: "/vibe-pass",
                  source_section: "homepage_promo",
                });
                void trackPlatformEvent("vibe_pass_promo_click", {
                  page_path: "/",
                  category: "vibe_pass",
                  metadata_json: { source_section: "homepage_promo" },
                });
              }}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#f0c66a] via-[#e7a94f] to-fuchsia-500 px-5 py-3 text-sm font-black text-black shadow-[0_18px_42px_rgba(240,198,106,0.24)] transition hover:scale-[1.01]"
            >
              <CheckCircle2 size={17} />
              Join Free Pilot Access
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
